import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { COUNTRY_CODE } from '@core/enums';
import { Observable, map, shareReplay, firstValueFrom } from 'rxjs';
import { environment } from '@core/envs';
import {
  AutoSuggest,
  ICoordate,
  IDealerByLocation,
  IRoutesTravel,
  ISuggest,
  ITravelSumary
} from '@core/interface';
import { GetCountryService } from '../country/country.service';

@Injectable({
  providedIn: 'root'
})
export class HeremapsEndpointsService {
  readonly #getCountryService = inject(GetCountryService);
  readonly #http = inject(HttpClient);
  readonly #key = environment.heremaps.key;
  dealer!: IDealerByLocation;
  dealerId = "";

  revgeocode(geocode: string): Observable<ISuggest[]> {
    const url = `https://revgeocode.search.hereapi.com/v1/revgeocode?apikey=${this.#key}&at=${geocode}`;
    return this.#http.get<{ items: AutoSuggest }>(url).pipe(
      map((search) => search.items)
    );
  }

  autoComplete(search: string): Observable<ISuggest[]> {
    const url = `https://autocomplete.search.hereapi.com/v1/autocomplete?apiKey=${this.#key}&q=${search}&${this.#getQueryByCountryToUrl()}`;
    return this.#http.get<{ items: AutoSuggest }>(url).pipe(
      map((search) => search.items)
    );
  }

  lookup(id: string): Observable<ISuggest> {
    const url = `https://lookup.search.hereapi.com/v1/lookup?apiKey=${this.#key}&id=${id}`;
    return this.#http.get<ISuggest>(url);
  }

  autosuggest(search: string): Observable<ISuggest[]> {
    const coordinateDefault = this.#getPositionDefaultByCountry();
    const url = `https://autosuggest.search.hereapi.com/v1/autosuggest?apiKey=${this.#key}&at=${coordinateDefault.lat},${coordinateDefault.lng}&q=${search}&${this.#getQueryByCountryToUrl()}&limit=5`;

    return this.#http.get<{ items: AutoSuggest }>(url).pipe(
      map((search) => search.items)
    );
  }

  readonly #getQueryByCountryToUrl = (): string => {
    const country = this.#getCountryService.getCountry();
    return `in=countryCode%3A${country}`;
  };

  #getCalculateRoute(origin: ICoordate, destination: ICoordate):
    Observable<number> {
    const url = `https://router.hereapi.com/v8/routes?transportMode=car&origin=${origin.lat},${origin.lng}&destination=${destination.lat},${destination.lng}&return=travelSummary&apikey=${this.#key}&units=metric&routingMode=short`;
    return this.#http.get<IRoutesTravel>(url)
      .pipe(
        shareReplay(),
        map(resp => this.#getTravel(resp))
      );
  }

  #getTravel(resp: IRoutesTravel): number {
    const travel: ITravelSumary = resp.routes[0]?.sections[0]?.travelSummary;
    return travel.length;
  }

  getDistance = async (origin: ICoordate, destination: ICoordate): Promise<number> => {
    let distance = -1;
    try {
      distance = await firstValueFrom(this.#getCalculateRoute(origin, destination))
    } catch (error) {
      console.error(error);
    }
    const roundDistance = distance <= 1000 ? 1 : distance;
    return roundDistance > 1 ? Number((roundDistance / 1000).toFixed(1)) : roundDistance;
  }

  public getDealerSelected(): IDealerByLocation {
    return this.dealer;
  }

  readonly #getPositionDefaultByCountry = (): ICoordate => {
    const country = this.#getCountryService.getCountry();
    const defaultCoordinates: ICoordate = { lat: -23.6549085, lng: -46.5913373 };

    const countryCoordinates: Record<string, ICoordate> = {
      [COUNTRY_CODE.BRAZIL]: { lat: -23.6549085, lng: -46.5913373 },
      [COUNTRY_CODE.ARGENTINA]: { lat: -34.60849, lng: -58.37344 },
      [COUNTRY_CODE.CHILE]: { lat: -33.4377756, lng: -70.6504502 },
      [COUNTRY_CODE.PERU]: { lat: -12.0621065, lng: -77.0365256 },
      [COUNTRY_CODE.ECUADOR]: { lat: -0.1806532, lng: -78.4678382 },
      [COUNTRY_CODE.COLOMBIA]: { lat: 6.230833, lng: -75.590553 },
      [COUNTRY_CODE.VENEZUELA]: { lat: -0.1806532, lng: -78.4678382 }
    };

    if (!country || !(country in countryCoordinates)) return defaultCoordinates;

    return countryCoordinates[country];
  }
}
